import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
} from "@mui/material";
import ContextOrganizationField, {
  ContextOrganizationFieldMode,
} from "components/ContextOrganizationField";
import SelectEntity from "components/SelectEntity";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import { pick } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { Field, FormProps, withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  AdminListRequestOfOrientationFilter,
  CertificateRequest,
  ListResponseOfCertificateTemplate,
} from "services/KnowfullyAdminClient";
import certificateTemplateInfoStore from "stores/certificateStore";
import contextStore from "stores/contextStore";
import { SnackbarMessageProps } from "types/common-types";
import { CertificateAccordionsProps } from "./CertificateAccordions";

type CertificateFormProps = {} & CertificateAccordionsProps;

const { Form } = withTypes<CertificateRequest>();

const CertificateForm = ({
  entity: certificate,
  submitLabel,
  onSubmit,
  onCancel,
}: CertificateFormProps & FormProps<CertificateRequest>) => {
  const { organizationId } = contextStore.useState();

  const {
    certificateTemplateId,
    isLandscape,
  } = certificateTemplateInfoStore.useState();

  const { certificateService } = useGlobalContext();
  const { formatMessage: t } = useIntl();

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const init = useMemo(
    () => ({
      ...pick(certificate ?? {}, [
        "id",
        "name",
        "value",
        "description",
        "organizationId",
        "certificateTemplateId",
        "certificateThemeId",
      ]),
    }),
    [certificate],
  );

  const fetchedCertificateThemes = useQuery<ListResponseOfCertificateTemplate>(
    ["certificateThemes", organizationId, isLandscape],
    () =>
      certificateService.getCertificateThemes(
        AdminListRequestOfOrientationFilter.fromJS({
          organizationId,
          sortField: "name",
          parameters: {
            isLandscape: isLandscape,
          },
        }),
      ),
    {
      enabled: certificateTemplateId != null,
    },
  );

  const fetchedCertificateTemplates = useQuery<ListResponseOfCertificateTemplate>(
    ["certificateTemplates", organizationId],
    () =>
      certificateService.getCertificateTemplates(
        AdminListRequestOfOrientationFilter.fromJS({
          organizationId,
          sortField: "name",
        }),
      ),
  );

  useEffect(() => {
    if (
      certificate?.certificateTemplateId &&
      fetchedCertificateTemplates.data?.data
    ) {
      const template = fetchedCertificateTemplates.data.data.find(
        (x) => x.id === certificate.certificateTemplateId,
      );
      if (template) {
        certificateTemplateInfoStore.update((s) => {
          s.certificateTemplateId = certificate.certificateTemplateId;
          s.isLandscape = template.isLandscape!;
        });
      }
    }
  }, [certificate?.certificateTemplateId, fetchedCertificateTemplates.data]);

  return useMemo(() => {
    const updateTemplateInfo = (certificateTemplateId: string | undefined) => {
      if (certificateTemplateId == null) {
        certificateTemplateInfoStore.update((s) => {
          s.certificateTemplateId = undefined;
          s.isLandscape = null;
        });
        return;
      }

      const certificateTemplate = fetchedCertificateTemplates.data?.data?.find(
        (x) => x.id === certificateTemplateId,
      );

      if (certificateTemplate != null) {
        certificateTemplateInfoStore.update((s) => {
          s.certificateTemplateId = certificateTemplateId;
          s.isLandscape = certificateTemplate.isLandscape!;
        });
      }
    };

    const onSubmitInternal = (values: CertificateRequest) => {
      return onSubmit(values);
    };

    return (
      <Form
        onSubmit={onSubmitInternal}
        initialValues={init}
        render={({ handleSubmit, submitting, submitError, values, form }) => {
          return (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <ContextOrganizationField
                  values={values}
                  formChange={form.change}
                  mode={
                    certificate
                      ? ContextOrganizationFieldMode.EditManuallyTriggered
                      : ContextOrganizationFieldMode.SetByHeader
                  }
                />

                <Field name="name" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        {...input}
                        required
                        fullWidth
                        label={t({ id: "certificateFormNameLabel" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="value" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        {...input}
                        required
                        fullWidth
                        label={t({ id: "certificateFormValueLabel" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="description">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        {...input}
                        multiline={true}
                        rows={4}
                        fullWidth
                        label={t({
                          id: "certificateFormDescriptionLabel",
                        })}
                        InputLabelProps={{ required: false }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="certificateTemplateId" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectEntity
                          fetchedData={fetchedCertificateTemplates}
                          labelField="name"
                          label={t({ id: "certificateTemplateLabel" })}
                          {...input}
                          required={true}
                          parentChange={(value) => {
                            return updateTemplateInfo(value);
                          }}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                            required: true,
                          }}
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>
                <Field name="certificateThemeId">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectEntity
                          fetchedData={fetchedCertificateThemes}
                          labelField="name"
                          label={t({ id: "certificateThemeLabel" })}
                          {...input}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                          disabled={values.certificateTemplateId == null}
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>
              </Grid>
              <Box mb={2} />
              {submitError && <Alert severity="error">{submitError}</Alert>}
              <DialogActions>
                {submitting && <CircularProgress size={24} />}
                <Button disabled={submitting} onClick={onCancel}>
                  {t({ id: "cancelButton" })}
                </Button>

                <Button type="submit" color="primary" disabled={submitting}>
                  {submitLabel}
                </Button>
              </DialogActions>
              <SnackbarMessage
                snackbarMessage={snackbarMessage}
                setSnackbarMessage={setSnackbarMessage}
              />
            </form>
          );
        }}
      />
    );
  }, [
    certificate,
    fetchedCertificateTemplates,
    fetchedCertificateThemes,
    init,
    onCancel,
    onSubmit,
    snackbarMessage,
    submitLabel,
    t,
  ]);
};

export default CertificateForm;
